'use client'

import Loader from "@/app/loading";
import { toast } from "@/hooks/use-toast";
import useOrderStore from "@/store/orderStore";
import useUserOrderedStore from "@/store/userOrderedStore";
import { Star } from "lucide-react";
import Link from "next/link"
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button"
import useCommonStore from "@/store/commonStore";
import { GTMUpdateOrderSummaryHandler } from "@/lib/analytics";

interface IObj {
  [key: string]: any
}

export default function OrderList() {
  const [loading, setLoading] = useState(false);
  const [reordering, setReordering] = useState<string | null>(null); // track which order is being reordered

  const { orderedLists, orderDetails, orderLists } = useUserOrderedStore()
  const { updateUserOrderSummary } = useOrderStore()
  const {translations}=useCommonStore()
  const router = useRouter();

  useEffect(() => {
    loadOrders();
  }, []);


  async function loadOrders() {
    setLoading(true);
    const result = await orderedLists();
    setLoading(false);
    if (!result.status) {
      toast({
        variant: "destructive",
        title: "Error",
        description: result.error,
      });
    }
  }  

  const updateOrderSummary = async (items: { [key: string]: any }[],order:IObj) => {
    GTMUpdateOrderSummaryHandler({
      items: order?.items.map((item:IObj) => ({
        item_id: item.pdt_id,
        item_variant: item?.variant_id,
        quantity: item.quantity,
        price:item.gross_prc
      }))
    })
    const result = await updateUserOrderSummary({ items });
    if (result?.status) {
      router.push("/checkout");
    } else {
      toast({
        title: "Error",
        description: result?.message || result?.error,
        variant: "destructive"
      });
    }
  };

  const handleReorder = async (order: IObj) => {
    try {
      setReordering(order.order_id);
      const items = order.items.map((item: IObj) => ({
        pdt_id: item.pdt_id,
        variant_id: item.variant_id,
        quantity: item.quantity
      }));
      await updateOrderSummary(items,order);
    } finally {
      setReordering(null);
    }
  };

  return (
    <>
      {
        loading ? (
          <Loader />
        ) : (
          (!orderLists || !orderLists?.length) ? (
            <div className="text-center">
              <figure className="w-[300px] m-auto mb-[20px]">
                <img src="https://curlified.com/assets/wishlist.4ffd6550.png" alt="empty wishlist" className="cf-img-cover" />
              </figure>
              <p className="text-lg mb-[30px]">{translations["empty_order"]||"Your Orders are empty."}</p>
              <Button className="bg-primary text-white" onClick={() => router.push("/")}>{translations["shop_now"]||"Shop Now"}</Button>
            </div>
          ) :
          <div>
            {/* TODO: just keeping to complete the flow */}
            {/* <p className="cursor-pointer font-semibold" onClick={() => router.push("/profile")}>Back</p> */}
            <h1 className="text-3xl md:text-7xl font-bold text-chart-3 mb-4 md:mb-8 hidden md:block">{translations["all_orders"]||"All Orders"}</h1>
            <div className="flex flex-col gap-6">
              {orderLists?.map(order => (
                <div key={order.order_id} className="bg-white border rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    
                    <span className={`font-semibold text-green-600`}>
                      {translations["order"]||"Order"} {order.status_nm }
                    </span>
                    <span className="text-gray-500 ml-2 text-sm">{translations["ordered_on"]||"Ordered on"} {order.created_at}</span>
                  </div>
                  <p className="mb-1 text-gray-500 ml-2 text-sm"># {order.order_id} </p>
                  <div className="flex flex-col gap-3 bg-[#FFFCF2] rounded-lg p-4 mb-4">
                    {order.items.map((item: IObj) => (
                      <div key={item.pdt_id} className="border-b border-b-[#F3E9DE] last:border-b-0 pb-2 last:pb-0">
                        <div className="flex items-center gap-3 mb-2">
                          <img src={item.thumbnail} alt={item.pdt_nm} className="w-12 h-12 rounded" />
                          <span className="text-sm">{item.pdt_nm}</span>  
                        </div>
                        {order.status == "10" && (
                          item.rating ? (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500">{translations["rating"]||"Rating"}</span>
                              <div className="flex gap-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                  <Star
                                    key={star}
                                    className={`w-4 h-4 ${item?.rating >= star
                                        ? "fill-yellow-400 text-yellow-400"
                                        : "text-gray-300"
                                      }`}
                                  />
                                ))}
                              </div>
                            </div>
                          ) : (
                            <Link
                              href={`/profile/orders/${order.order_id}`}
                              className="flex gap-1 text-xs"
                            >
                              {translations["rate_prod"]||"Rate product"}
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star key={star} className="w-4 h-4 text-gray-300" />
                              ))}
                            </Link>
                          )
                        )}
                      </div>
                      
                    ))}
                    {/* {order.status && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-gray-500">Rate product</span>
                        {[...Array(5)].map((_, i) => (
                          <span key={i} className="text-[#FFD600] text-lg">★</span>
                        ))}
                      </div>
                    )} */}
                  </div>
                  <div className="flex gap-4">
                    <button className="bg-primary text-white px-4 py-2 rounded font-semibold" onClick={()=>handleReorder(order)}>{translations["re_order"]||"Re order"}</button>
                    <Link
                      href={`/profile/orders/${order.order_id}`}
                      className="bg-primary text-white px-4 py-2 rounded font-semibold text-center"
                    >
                      {translations["view_ordr"]||"View order"}
                    </Link>
                  </div>
                  {/* <div className="flex gap-4">
                    <button className="bg-primary text-white px-8 py-2 rounded font-semibold">Re order</button>
                    <Link
                      href={`/profile/orders/${order.order_id}`}
                      className="bg-primary text-white px-8 py-2 rounded font-semibold text-center"
                    >
                      View order
                    </Link>
                  </div> */}
                </div>
              ))}
            </div>
          </div>
        )
      }
    </>
  )
}